import {
  TLocationDetails,
  TRESPONSE_GET_MACHINE_TICKET_HISTORY,
  TRESPONSE_GET_MAINTENANCE_TICKET_HISTORY,
} from '@monorepo/types';
import { TAccordionTableVM } from '../../model/accordion-table.types';
import { TICKET_LIST_HELPER } from './ticket-list-helper';
import * as _ from 'lodash';
import moment from 'moment';
import { ACCORDION_TABLE_MODIFIER } from '../../model/accordion-table.modifier';

export const MACHINE_HISTORY_ACCORDION_TABLE_MODEL = {
  createModel(
    tickets?: TRESPONSE_GET_MACHINE_TICKET_HISTORY,
    locationDisplayCallBack?: (location: TLocationDetails['location']) => string[],
  ): TAccordionTableVM {
    return ACCORDION_TABLE_MODIFIER.createModel(
      [
        { label: 'tm_container_labels.Date&Time', colSize: 7 },
        { label: 'tm_container_labels.MachineIssueHeader', colSize: 9 },
        { label: 'tm_container_labels.LocationHeader', colSize: 7 },
      ],
      tickets
        ? tickets.ticketDetails.map((ticket) => {
            return {
              id: ticket.ticketId,
              tag: _.compact([
                ticket.metadata.reissuedCount > 0
                  ? {
                      displayName: 'tm_container_labels.Re-issued',
                      frequency: ticket.metadata.reissuedCount,
                    }
                  : undefined,
              ]),
              isMinimized: true,
              rowItems: [
                {
                  type: 'DATE_AND_TIME',
                  timestamp: ticket.createdAt,
                },
                { type: 'TEXT', value: ticket.breakdownReasonDetails?.reason || '--' },
                { type: 'TEXT', value: ticket.ticketLocationDetails.locationCode },
              ],
              moreInfo: [
                {
                  items: [
                    {
                      size: 12,
                      label: 'tm_container_labels.MachineSerialID',
                      values: TICKET_LIST_HELPER.mapToValuesList([tickets.machineDescription.factorySerialNumber]),
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.Solution',
                      values: ticket.solutionOpt ? TICKET_LIST_HELPER.mapToValuesList([ticket.solutionOpt]) : [],
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.Assignee',
                      values: ticket.mechanicDetails
                        ? TICKET_LIST_HELPER.mapToValuesList([ticket.mechanicDetails.employeeName])
                        : [],
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.DownTime',
                      values:
                        ticket.downTimeInMins !== undefined
                          ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.downTimeInMins)
                          : [],
                      type: 'DURATION',
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.MachineType',
                      values: TICKET_LIST_HELPER.mapToValuesList([tickets.machineDescription.machineType]),
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.ResponseTime',
                      values:
                        ticket.grabTimeInMins !== undefined
                          ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.grabTimeInMins)
                          : [],
                      type: 'DURATION',
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.Category',
                      values: TICKET_LIST_HELPER.mapToValuesList([tickets.machineDescription.machineCategory]),
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.RepairTime',
                      values:
                        ticket.repairedTimeInMins !== undefined
                          ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.repairedTimeInMins)
                          : [],
                      type: 'DURATION',
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.Sub-Category',
                      values: tickets.machineDescription.machineSubCategory
                        ? TICKET_LIST_HELPER.mapToValuesList([tickets.machineDescription.machineSubCategory])
                        : [],
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.EndTime',
                      values: ticket.endTimeInMins
                        ? TICKET_LIST_HELPER.mapToValuesList([TICKET_LIST_HELPER.formatTimeStamp(ticket.endTimeInMins)])
                        : [],
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.TicketID',
                      values: TICKET_LIST_HELPER.mapToValuesList([ticket.ticketId.toString()]),
                    },
                    {
                      size: 24,
                      label: 'tm_container_labels.MachineIssue',
                      values: ticket.breakdownReasonDetails?.reason
                        ? TICKET_LIST_HELPER.mapToValuesList([ticket.breakdownReasonDetails?.reason])
                        : [],
                    },
                    {
                      size: 24,
                      label: 'tm_container_labels.AdditionalInfo',
                      values: ticket.description ? TICKET_LIST_HELPER.mapToValuesList([ticket.description]) : [],
                    },
                    {
                      size: 24,
                      label: 'tm_container_labels.Location',
                      values: locationDisplayCallBack
                        ? TICKET_LIST_HELPER.getLocation(
                            locationDisplayCallBack(ticket.ticketLocationDetails.location),
                            ticket.ticketLocationDetails.workstationInfoOpt?.workstationName,
                          )
                        : [{ displayName: '--' }],
                      type: 'LOCATION',
                    },
                  ],
                },
              ],
              actions: [],
            };
          })
        : [],
    );
  },
};

export const MAINTENANCE_HISTORY_ACCORDION_TABLE_MODEL = {
  createModel(tickets?: TRESPONSE_GET_MAINTENANCE_TICKET_HISTORY): TAccordionTableVM {
    return ACCORDION_TABLE_MODIFIER.createModel(
      [
        { label: 'tm_container_labels.ScheduledDate', colSize: 7 },
        { label: 'tm_container_labels.MachineSerialIDheader', colSize: 9 },
        { label: 'tm_container_labels.LocationHeader', colSize: 7 },
      ],
      tickets
        ? tickets.ticketDetails.map((ticket) => {
            return {
              id: ticket.ticketId,
              tag: undefined,
              isMinimized: true,
              rowItems: [
                {
                  type: 'TEXT',
                  value: moment(ticket.dueAt).format('DD/MM/YYYY'),
                },
                { type: 'TEXT', value: tickets.machineDescription.factorySerialNumber },
                { type: 'TEXT', value: ticket.locationInfo.locationDisplayCode },
              ],
              moreInfo: [
                {
                  items: [
                    {
                      size: 12,
                      label: 'tm_container_labels.MachineSerialID',
                      values: TICKET_LIST_HELPER.mapToValuesList([tickets.machineDescription.factorySerialNumber]),
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.StartTime',
                      values: ticket.startedAt
                        ? TICKET_LIST_HELPER.mapToValuesList([TICKET_LIST_HELPER.formatTimeStamp(ticket.startedAt)])
                        : [],
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.MachineType',
                      values: TICKET_LIST_HELPER.mapToValuesList([tickets.machineDescription.machineType]),
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.EndTime',
                      values: ticket.completedAt
                        ? TICKET_LIST_HELPER.mapToValuesList([TICKET_LIST_HELPER.formatTimeStamp(ticket.completedAt)])
                        : [],
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.Category',
                      values: TICKET_LIST_HELPER.mapToValuesList([tickets.machineDescription.machineCategory]),
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.MaintenanceTime',
                      values:
                        ticket.maintenanceTimeInMinutes !== undefined
                          ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.maintenanceTimeInMinutes)
                          : [],
                      type: 'DURATION',
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.Sub-Category',
                      values: tickets.machineDescription.machineSubCategory
                        ? TICKET_LIST_HELPER.mapToValuesList([tickets.machineDescription.machineSubCategory])
                        : [],
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.DownTime',
                      values:
                        ticket.downTimeInMinutes !== undefined
                          ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.downTimeInMinutes)
                          : [],
                      type: 'DURATION',
                    },
                    {
                      size: 12,
                      label: 'Location Code',
                      values: TICKET_LIST_HELPER.mapToValuesList([ticket.locationInfo.locationDisplayCode]),
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.Assignee',
                      values: ticket.mechanicDetails
                        ? TICKET_LIST_HELPER.mapToValuesList([ticket.mechanicDetails.employeeName])
                        : [],
                    },
                    {
                      size: 12,
                      label: 'tm_container_labels.Checklist',
                      values: ticket.serviceTasks.map((checklist) => {
                        return {
                          displayName: checklist.task,
                          icon: checklist.isCompleted ? 'assets/icon/ticket-management/green-checkmark.svg' : undefined,
                        };
                      }),
                    },
                  ],
                },
              ],
              actions: [],
            };
          })
        : [],
    );
  },
};
